import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { styled } from "@pigment-css/react";
import { usePopover } from "./usePopover";
import {
  CheckMark,
  ItemLabel,
  SearchableSelectPopover,
  SearchInput,
  SearchWrapper,
  SELECT_SEARCH_THRESHOLD,
  SelectEmptyState,
  SelectItem,
  SelectItemsContainer,
  SelectPopover,
  SelectTrigger,
} from "./selectParts";

const Popover = styled(SelectPopover)(() => ({
  minWidth: "140px",
}));

const SearchPopover = styled(SearchableSelectPopover)(() => ({
  minWidth: "140px",
}));

export interface SingleSelectOption {
  key: string;
  label: string;
}

interface SingleSelectProps {
  options: SingleSelectOption[];
  value: string;
  disabled?: boolean;
  placeholder?: string;
  searchPlaceholder?: string;
  noResultsText?: string;
  onChange: (value: string) => void;
}

export const SingleSelect = ({
  options,
  value,
  disabled,
  placeholder = "—",
  searchPlaceholder,
  noResultsText,
  onChange,
}: SingleSelectProps) => {
  const { isOpen, position, triggerRef, popoverRef, toggle, close } = usePopover();
  const [search, setSearch] = useState("");
  const searchRef = useRef<HTMLInputElement>(null);

  const isSearchable = options.length > SELECT_SEARCH_THRESHOLD;

  const selectedLabel = useMemo(() => {
    if (!value) return placeholder;

    return options.find((o) => o.key === value)?.label ?? value;
  }, [options, value, placeholder]);

  const filteredOptions = useMemo(() => {
    if (!isSearchable || !search) return options;

    const query = search.toLowerCase();

    return options.filter((o) => o.label.toLowerCase().includes(query));
  }, [options, search, isSearchable]);

  const handleSelect = (key: string) => {
    onChange(key);
    close();
  };

  useEffect(() => {
    if (!isOpen) setSearch("");
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && isSearchable) {
      requestAnimationFrame(() => searchRef.current?.focus());
    }
  }, [isOpen, isSearchable]);

  const items = filteredOptions.map((option) => (
    <SelectItem key={option.key} onClick={() => handleSelect(option.key)}>
      <ItemLabel>{option.label}</ItemLabel>
      {option.key === value && <CheckMark />}
    </SelectItem>
  ));

  return (
    <>
      <SelectTrigger ref={triggerRef} type="button" disabled={disabled} onClick={toggle}>
        {selectedLabel}
      </SelectTrigger>
      {isOpen &&
        createPortal(
          isSearchable ? (
            <SearchPopover ref={popoverRef} style={position}>
              <SearchWrapper>
                <SearchInput
                  ref={searchRef}
                  type="text"
                  placeholder={searchPlaceholder}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </SearchWrapper>
              <SelectItemsContainer>
                {filteredOptions.length === 0
                  ? noResultsText && <SelectEmptyState>{noResultsText}</SelectEmptyState>
                  : items}
              </SelectItemsContainer>
            </SearchPopover>
          ) : (
            <Popover ref={popoverRef} style={position}>
              {items}
            </Popover>
          ),
          document.body,
        )}
    </>
  );
};
